
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Edit, Search, UserPlus, ChevronLeft, ChevronRight, MoreHorizontal, X, Mail, Eye, EyeOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth';
import { useSuperAdminUsers } from '@/hooks/useSuperAdminUsers'; // We'll create this hook

// Helper functions moved to utils for cleaner component
import { 
  getRoleDisplayName, 
  getRoleBadgeVariant,
  getRoleDescription,
} from '@/utils/role-utils';

interface SuperAdminUsersTabProps {
  organisations: any[];
}

const SuperAdminUsersTab: React.FC<SuperAdminUsersTabProps> = ({ organisations }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(true); // Default to showing password
  
  // New user form state
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    role: 'new_sign_up',
    organisation_id: '',
    password: ''
  });
  
  // Edit user form state
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    organisation_id: ''
  });
  
  // User role change state
  const [roleChangeForm, setRoleChangeForm] = useState({
    role: ''
  });
  
  // User to reset password for
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  
  // Form validation states
  const [newUserErrors, setNewUserErrors] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Fetch users with our custom hook
  const { 
    users, 
    totalUsers, 
    isLoading, 
    error,
    refetch,
    fetchUserDetail,
    createUser,
    updateUser,
    updateUserRole,
    sendPasswordReset
  } = useSuperAdminUsers({
    page,
    limit: pageSize,
    search: searchQuery,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    organisation_id: orgFilter !== 'all' ? orgFilter : undefined
  });
  
  // Total pages calculation
  const totalPages = Math.ceil(totalUsers / pageSize);
  
  // Handle opening user details
  const handleViewUser = async (userId: string) => {
    try {
      const userData = await fetchUserDetail(userId);
      if (userData) {
        setSelectedUser(userData);
        setEditUserForm({
          name: userData.user.name || '',
          organisation_id: userData.user.organisation_id || ''
        });
        setRoleChangeForm({
          role: userData.user.role
        });
        setUserDetailOpen(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not fetch user details. Please try again.',
        variant: 'destructive'
      });
      console.error('Error fetching user details:', error);
    }
  };
  
  // Handle creating a new user
  const handleCreateUser = async () => {
    // Validate form
    const errors = {
      name: !newUserForm.name ? 'Name is required' : '',
      email: !newUserForm.email ? 'Email is required' : (!newUserForm.email.includes('@') ? 'Invalid email format' : ''),
      password: !newUserForm.password ? 'Password is required' : 
               (newUserForm.password.length < 8 ? 'Password must be at least 8 characters' : '')
    };
    
    setNewUserErrors(errors);
    
    // Check if we have any errors
    if (errors.name || errors.email || errors.password) {
      return;
    }
    
    try {
      await createUser(newUserForm);
      
      // Reset form and close dialog
      setNewUserForm({
        name: '',
        email: '',
        role: 'user',
        organisation_id: '',
        password: ''
      });
      
      setAddUserOpen(false);
      setShowPassword(true); // Reset to default showing password
      
      // Show success message
      toast({
        title: 'User Created',
        description: `User ${newUserForm.name} has been created successfully.`
      });
      
      // Refresh the user list
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not create user. Please try again.',
        variant: 'destructive'
      });
      console.error('Error creating user:', error);
    }
  };
  
  // Handle updating a user
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUser(selectedUser.user.id, editUserForm);
      
      // Show success message
      toast({
        title: 'User Updated',
        description: `User ${selectedUser.user.name} has been updated successfully.`
      });
      
      // Refresh the user list and detail
      const updatedUser = await fetchUserDetail(selectedUser.user.id);
      setSelectedUser(updatedUser);
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not update user. Please try again.',
        variant: 'destructive'
      });
      console.error('Error updating user:', error);
    }
  };
  
  // Handle changing a user's role
  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    
    try {
      const result = await updateUserRole(selectedUser.user.id, roleChangeForm.role);
      
      // Show success message
      toast({
        title: 'Role Updated',
        description: `User role has been updated to ${getRoleDisplayName(roleChangeForm.role)}.`
      });
      
      // If the current user's role was changed, they need to refresh their token
      if (result.requires_refresh) {
        toast({
          title: 'Session Update Required',
          description: 'Your role has been changed. You will be logged out to refresh your session.',
          duration: 5000
        });
      }
      
      // Refresh the user list and detail
      const updatedUser = await fetchUserDetail(selectedUser.user.id);
      setSelectedUser(updatedUser);
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not update user role. Please try again.',
        variant: 'destructive'
      });
      console.error('Error updating user role:', error);
    }
  };
  
  // Handle sending password reset email
  const handleSendPasswordReset = async () => {
    if (!resetPasswordUserId) return;
    
    try {
      await sendPasswordReset(resetPasswordUserId);
      
      // Show success message
      toast({
        title: 'Password Reset Email Sent',
        description: 'A password reset email has been sent to the user.'
      });
      
      // Close the dialog
      setResetPasswordOpen(false);
      setResetPasswordUserId(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not send password reset email. Please try again.',
        variant: 'destructive'
      });
      console.error('Error sending password reset:', error);
    }
  };
  
  // Get organisation name for a user
  const getOrganisationName = (orgId: string) => {
    const org = organisations.find(o => o.id === orgId);
    return org ? org.name : 'None';
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">System Users</h2>
        
        <div className="flex w-full sm:w-auto gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search users..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select
            defaultValue="all"
            value={roleFilter}
            onValueChange={(value) => setRoleFilter(value)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="org_admin">Org Admin</SelectItem>
              <SelectItem value="rest_admin">Rest Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="new_sign_up">New Sign Up</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            defaultValue="all"
            value={orgFilter}
            onValueChange={(value) => setOrgFilter(value)}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Organisations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organisations</SelectItem>
              <SelectItem value="none">No Organisation</SelectItem>
              {organisations.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={addUserOpen} onOpenChange={(open) => {
            setAddUserOpen(open);
            if (open) {
              setShowPassword(true); // Reset to default showing password when opening
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user to the system. They will receive an email to set their password.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                    className="col-span-3"
                  />
                  {newUserErrors.name && (
                    <div className="col-span-4 -mt-3 text-right text-sm text-destructive">
                      {newUserErrors.name}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="email" className="text-right">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    className="col-span-3"
                  />
                  {newUserErrors.email && (
                    <div className="col-span-4 -mt-3 text-right text-sm text-destructive">
                      {newUserErrors.email}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="password" className="text-right">
                    Password
                  </Label>
                  <div className="col-span-3 relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={newUserForm.password}
                      onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                      className="pr-10"
                      placeholder="Initial password for the user"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  {newUserErrors.password && (
                    <div className="col-span-4 -mt-3 text-right text-sm text-destructive">
                      {newUserErrors.password}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="role" className="text-right">
                    Role
                  </Label>
                  <Select
                    value={newUserForm.role}
                    onValueChange={(value) => setNewUserForm({ ...newUserForm, role: value })}
                  >
                    <SelectTrigger id="role" className="col-span-3">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_sign_up">
                        New Sign Up
                      </SelectItem>
                      <SelectItem value="rest_admin">
                        Restaurant Admin
                      </SelectItem>
                      <SelectItem value="org_admin">
                        Organisation Admin
                      </SelectItem>
                      <SelectItem value="user">
                        User
                      </SelectItem>
                      <SelectItem value="in_progress">
                        In Progress
                      </SelectItem>
                      {user?.role === 'super_admin' && (
                        <SelectItem value="super_admin">
                          Super Admin
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="organisation" className="text-right">
                    Organisation
                  </Label>
                  <Select
                    value={newUserForm.organisation_id || "none"}
                    onValueChange={(value) => {
                      const newOrgId = value === "none" ? "" : value;
                      setNewUserForm({ ...newUserForm, organisation_id: newOrgId });
                    }}
                  >
                    <SelectTrigger id="organisation" className="col-span-3">
                      <SelectValue placeholder="Select organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {organisations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setAddUserOpen(false);
                  setShowPassword(true); // Reset to default showing password
                }}>
                  Cancel
                </Button>
                <Button onClick={handleCreateUser}>
                  Create User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-destructive">
                    Error loading users: {error}
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="group">
                    <TableCell>
                      <button
                        onClick={() => handleViewUser(user.id)}
                        className="font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                      >
                        {user.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleViewUser(user.id)}
                        className="cursor-pointer transition-colors hover:text-primary active:text-primary"
                      >
                        {user.email}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {user.role === 'super_admin' && (
                            <div className="flex space-x-0.5">
                              <div className="w-2 h-2 bg-destructive rounded-full"/>
                              <div className="w-2 h-2 bg-destructive rounded-full"/>
                              <div className="w-2 h-2 bg-destructive rounded-full"/>
                            </div>
                          )}
                          {user.role === 'org_admin' && (
                            <div className="flex space-x-0.5">
                              <div className="w-2 h-2 bg-primary rounded-full"/>
                              <div className="w-2 h-2 bg-primary rounded-full"/>
                            </div>
                          )}
                          {user.role === 'rest_admin' && (
                            <div className="flex space-x-0.5">
                              <div className="w-2 h-2 bg-primary rounded-full"/>
                            </div>
                          )}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant={getRoleBadgeVariant(user.role)}>
                                {getRoleDisplayName(user.role)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{getRoleDescription(user.role)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                    <TableCell>{user.organisation_id ? user.organisation_name || getOrganisationName(user.organisation_id) : '—'}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleViewUser(user.id)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setResetPasswordUserId(user.id);
                            setResetPasswordOpen(true);
                          }}>
                            <Mail className="h-4 w-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination controls */}
          {!isLoading && !error && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {users.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm">
                  Page {page} of {totalPages || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">Profile Details</TabsTrigger>
                <TabsTrigger value="role">Role Management</TabsTrigger>
                <TabsTrigger value="access">Access Permissions</TabsTrigger>
              </TabsList>
              
              {/* Profile Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-4 sm:col-span-1">
                    <Label htmlFor="edit-name">Name</Label>
                    <Input 
                      id="edit-name"
                      value={editUserForm.name}
                      onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input 
                      id="edit-email"
                      type="email"
                      value={selectedUser.user.email}
                      disabled
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-1">
                    <Label htmlFor="edit-role">Role</Label>
                    <Input 
                      id="edit-role"
                      value={getRoleDisplayName(selectedUser.user.role)}
                      disabled
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label htmlFor="edit-organisation">Organisation</Label>
                    <Select
                      value={editUserForm.organisation_id || "none"}
                      onValueChange={(value) => {
                        const newOrgId = value === "none" ? "" : value;
                        setEditUserForm({...editUserForm, organisation_id: newOrgId});
                      }}
                    >
                      <SelectTrigger id="edit-organisation">
                        <SelectValue placeholder="No Organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {organisations.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label>Created</Label>
                    <Input 
                      value={new Date(selectedUser.user.created_at).toLocaleString()}
                      disabled
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleUpdateUser}>
                    Save Changes
                  </Button>
                </div>
              </TabsContent>
              
              {/* Role Management Tab */}
              <TabsContent value="role" className="space-y-4">
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-md mb-4">
                    <h3 className="font-medium mb-1">About Roles</h3>
                    <p className="text-sm text-muted-foreground">
                      Roles determine what users can do in the system. Super Admin can access everything.
                      Organisation Admin can manage all restaurants within an organisation. Restaurant Admin
                      can manage specific restaurants.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="change-role">User Role</Label>
                    <Select
                      value={roleChangeForm.role}
                      onValueChange={(value) => setRoleChangeForm({role: value})}
                    >
                      <SelectTrigger id="change-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">
                          User
                        </SelectItem>
                        <SelectItem value="rest_admin">
                          Restaurant Admin
                        </SelectItem>
                        <SelectItem value="org_admin">
                          Organisation Admin
                        </SelectItem>
                        <SelectItem value="new_sign_up">
                          New Sign Up
                        </SelectItem>
                        <SelectItem value="in_progress">
                          In Progress
                        </SelectItem>
                        {user?.role === 'super_admin' && (
                          <SelectItem value="super_admin">
                            Super Admin
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    
                    <div className="p-3 border rounded-md mt-4">
                      <div className="text-sm font-medium">{getRoleDisplayName(roleChangeForm.role)}</div>
                      <div className="text-sm text-muted-foreground">
                        {getRoleDescription(roleChangeForm.role)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleUpdateRole}
                      disabled={roleChangeForm.role === selectedUser.user.role}
                    >
                      Update Role
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              {/* Access Permissions Tab */}
              <TabsContent value="access" className="space-y-4">
                <div className="p-4 bg-muted rounded-md mb-4">
                  <h3 className="font-medium mb-1">Access Permissions</h3>
                  <p className="text-sm text-muted-foreground">
                    This user has access to the following restaurants and organisations.
                    To manage these permissions, use the Permissions tab in Super Admin.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">Restaurant Access</h3>
                    {selectedUser.access.restaurants.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No restaurant access found.</p>
                    ) : (
                      <div className="border rounded-md max-h-[250px] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead>Restaurant</TableHead>
                              <TableHead>Access Level</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedUser.access.restaurants.map((access: any) => (
                              <TableRow key={access.id}>
                                <TableCell>{access.restaurant_name}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {access.access_level}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="font-medium mb-2">Organisation Access</h3>
                    {selectedUser.access.organisations.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No organisation access found.</p>
                    ) : (
                      <div className="border rounded-md max-h-[250px] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead>Organisation</TableHead>
                              <TableHead>Access Level</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedUser.access.organisations.map((access: any) => (
                              <TableRow key={access.id}>
                                <TableCell>{access.organisation_name}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {access.access_level}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send Password Reset Email</DialogTitle>
            <DialogDescription>
              This will send a password reset email to the user. They will need to click the link in the email to set a new password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            Are you sure you want to send a password reset email?
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordOpen(false);
              setResetPasswordUserId(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSendPasswordReset}>
              Send Reset Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminUsersTab;